<template>
  <button
    type="button"
    class="calendar-cell w-1/7 duration-25 relative flex h-12 items-center justify-center text-lg font-semibold"
    :class="{
      'text-text-neutral-primary': !isSelected && !isDisabled,
      'text-situational-grey': isDisabled,
    }"
    :disabled="isLoading || date.isOtherMonthDay"
    :data-is-selected="isSelected && !date.isOtherMonthDay"
    :data-qa-id="cellDataQaId"
    @click="handleCellClick"
  >
    <span
      v-if="!isLoading"
      class="calendar-selected-cells absolute w-full"
      :class="{
        'bg-background-neutral-onblock':
          isBetween && !date.isOtherMonthDay && isRange,
        'h-full w-full rounded-full': isFirst || isLast,
        'rounded-l-full': isFirstDateOfMonth,
        'rounded-r-full': isLastDateOfMonth,
        'after:-right-0.5':
          isFirst &&
          isRange &&
          !date.isOtherMonthDay &&
          !isLast &&
          !isLastDateOfMonth,
        'before:-left-0.5':
          isLast &&
          isRange &&
          !date.isOtherMonthDay &&
          !isFirst &&
          !isFirstDateOfMonth,
        'is-between h-6': isBetween,
      }"
    ></span>
    <span
      :class="[
        isSelected && !date.isOtherMonthDay
          ? 'bg-background-positive-primary text-static-white h-10 w-10 rounded-full'
          : 'h-full w-full',
        {
          'max-h-10 max-w-10 rounded-full border border-green-500':
            isMarked && !date.isOtherMonthDay,
        },
      ]"
      class="relative flex items-center justify-center duration-75"
    >
      <span v-if="isSelected || !isLoading">{{ dateText }}</span>
      <span
        v-else-if="!date.isOtherMonthDay"
        class="shimmer block h-6 w-8 rounded-2xl"
      ></span>
      <slot
        name="datePrice"
        v-bind="{
          datePrice,
          isSelected,
          isCheapest,
          isMarked,
        }"
      >
        <span
          v-if="!date.isOtherMonthDay && !isSelected && !isLoading && !isMarked"
          class="text-2xs text-text-positive-primary absolute bottom-0 w-full text-center font-medium"
          :class="{
            'text-text-neutral-primary': isSelected,
            'text-green-400': isCheapest && !isSelected,
            'text-gray-600': !isSelected && !isCheapest,
          }"
        >
          {{ datePrice }}
        </span>
      </slot>
    </span>
  </button>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import dayjs from 'dayjs';
  import { prettyPrice } from '@aviata/text-fns';
  import type { CalendarDate } from 'vue-renderless-calendar';

  const props = defineProps<{
    date: CalendarDate;
    price?: string;
    isLast?: boolean;
    isFirst?: boolean;
    isBetween?: boolean;
    isCheapest?: boolean;
    isSelected?: boolean;
    isDisabled?: boolean;
    isMarked?: boolean;
    isRange?: boolean;
    isFirstDateOfMonth?: boolean;
    isLastDateOfMonth?: boolean;
    isLoading?: boolean;
    isFiltered?: boolean;
  }>();

  const emit = defineEmits<{
    onSelect: [];
    onDisabledSelect: [];
  }>();

  const MILLION = 1_000_000;
  const ONE_HUNDRED_THOUSAND = 100_000;
  const THOUSAND = 1000;

  const dateText = computed(() =>
    !props.date.isOtherMonthDay ? props.date.day : '',
  );

  const datePrice = computed(() => {
    if (!props.price) return '';
    const price = parseInt(props.price);
    if (price >= MILLION) {
      const millions = Math.floor(price / MILLION);
      const thousands = String(price % MILLION).slice(0, 3);
      return `${millions},${thousands}M`;
    }
    if (price >= ONE_HUNDRED_THOUSAND) {
      const thousands = Math.floor(price / THOUSAND);
      return `${thousands}K`;
    }
    return `${prettyPrice(props.price)}`;
  });

  const cellDataQaId = computed(() => {
    if (props.date.isToday) {
      return 'calendarContainer.date.today';
    }
    const today = dayjs();
    const isYesterday = today.diff(props.date.formatted, 'd') === 1;
    if (isYesterday) return 'calendarContainer.date.yesterday';
    if (props.isDisabled && !props.date.isOtherMonthDay) {
      return 'calendarContainer.date.disabled';
    }
    if (!props.isDisabled && !props.date.isOtherMonthDay) {
      return 'calendarContainer.date.enabled';
    }
    return '';
  });

  const handleCellClick = () => {
    if (props.isDisabled) {
      emit('onDisabledSelect');
    } else {
      emit('onSelect');
    }
  };
</script>

<style scoped>
  .calendar-selected-cells::before,
  .calendar-selected-cells::after {
    @apply bg-background-neutral-onblock absolute top-[12px] h-6 w-2;
  }

  .calendar-cell:nth-child(7n) > .calendar-selected-cells::after {
    @apply bg-transparent;
  }

  .calendar-cell:nth-child(7n + 1) > .calendar-selected-cells::before {
    @apply bg-transparent;
  }

  .calendar-cell:nth-child(7n) > .calendar-selected-cells.is-between {
    border-top-right-radius: 10px;
    border-bottom-right-radius: 10px;
  }
  .calendar-cell:nth-child(7n + 1) > .calendar-selected-cells.is-between {
    border-top-left-radius: 10px;
    border-bottom-left-radius: 10px;
  }
</style>
